/* eslint-disable prefer-destructuring */
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(timezone);

export function parseDate(
  date: string | Date,
  formatted?: boolean,
  convertToLocal?: boolean,
  format?: string
) {
  if (convertToLocal) {
    return formatted
      ? dayjs(date).format(format || "MMMM D, YYYY")
      : dayjs(date).format(format || "MM/DD/YYYY");
  }
  if (formatted) {
    return dayjs.utc(date).format(format || "MMMM D, YYYY");
  }
  return dayjs.utc(date).format("MM/DD/YYYY");
}

export function parseTime(dateTime: string | Date, convertToLocal?: boolean) {
  const time = convertToLocal ? dayjs(dateTime) : dayjs(dateTime).utc();

  if (!time.hour() && !time.minute() && !time.second()) return "";
  return time.format("h:mm A");
}

export function parseDateInCST(date: string | Date, formatted?: boolean) {
  if (formatted) {
    return dayjs(date).tz("America/Chicago").format("MMMM D, YYYY"); // Use CST
  }

  return dayjs(date).tz("America/Chicago").format("MM/DD/YYYY"); // Use CST
}

export function parseTimeInCST(dateTime: string | Date) {
  const time = dayjs(dateTime).tz("America/Chicago"); // Convert to CST

  if (!time.hour() && !time.minute() && !time.second()) return "-";
  return time.format("h:mm A"); // Format time in CST
}

export function toUTCDate(value: string) {
  const date = new Date(value); // Parse the date
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  ).toISOString();
}

/** YYYY-MM-DD at start of string (ignores time / Z); backend sends calendar-only fields this way. */
const DATE_ONLY_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

export function parseDateOnlyParts(
  raw: string | null | undefined
): { y: number; m: number; d: number } | null {
  if (raw == null || raw === "") return null;
  const match = DATE_ONLY_PREFIX.exec(String(raw).trim());
  if (!match) return null;
  const y = Number(match[1]);
  const mo = Number(match[2]) - 1;
  const d = Number(match[3]);
  if (!Number.isFinite(y) || mo < 0 || mo > 11 || d < 1 || d > 31) {
    return null;
  }
  const dt = new Date(y, mo, d, 12, 0, 0, 0);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) {
    return null;
  }
  return { y, m: mo, d };
}

/** Display contract/base-rate date-only fields without timezone shift from ...Z ISO strings. */
export function formatDateOnlyForDisplay(
  raw: string | null | undefined,
  emptyLabel = "—"
): string {
  const parts = parseDateOnlyParts(raw);
  if (!parts) {
    return raw != null && String(raw).trim() !== "" ? String(raw) : emptyLabel;
  }
  return new Date(parts.y, parts.m, parts.d, 12, 0, 0, 0).toLocaleDateString();
}

/** PrimeReact Calendar `value` from API date-only strings. */
export function dateOnlyStringToCalendarDate(
  raw: string | null | undefined
): Date | null {
  const parts = parseDateOnlyParts(raw);
  if (!parts) return null;
  return new Date(parts.y, parts.m, parts.d, 12, 0, 0, 0);
}

export function compareDateOnlyStringsDesc(a: string, b: string): number {
  return String(b).localeCompare(String(a));
}

/** Request body: local calendar day as YYYY-MM-DD (not toISOString()). */
export function localCalendarDateToIsoDateString(
  d: Date | null | undefined
): string | undefined {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) {
    return undefined;
  }
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

/**
 * Normalize API date-only strings or calendar dates to YYYY-MM-DD for comparison.
 */
export function normalizeToIsoDateOnly(
  raw: string | Date | null | undefined
): string | undefined {
  if (raw == null) return undefined;
  if (raw instanceof Date) {
    return localCalendarDateToIsoDateString(raw);
  }
  const parts = parseDateOnlyParts(String(raw));
  if (!parts) return undefined;
  const mo = String(parts.m + 1).padStart(2, "0");
  const day = String(parts.d).padStart(2, "0");
  return `${parts.y}-${mo}-${day}`;
}

/** Stabilize Calendar onChange (local noon) to avoid UTC day drift in pickers. */
export function normalizeCalendarDayToLocalNoon(
  value: Date | null | undefined
): Date | null {
  if (
    value == null ||
    !(value instanceof Date) ||
    Number.isNaN(value.getTime())
  ) {
    return null;
  }
  return new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate(),
    12,
    0,
    0,
    0
  );
}

export function todayLocalNoon(): Date {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate(), 12, 0, 0, 0);
}

/** Local calendar date → `YYYY-MM-DD` (no timezone shift). */
export function formatLocalDateToIsoYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** `YYYY-MM-DD` → local Date at midnight (for date pickers). */
export function parseIsoYmdToLocalDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map((v) => parseInt(v, 10));
  return new Date(y, m - 1, d);
}

/** Sublabel under fee schedule prices (UTC calendar day from API). */
export function formatFeeContractEffectiveSublabel(
  isoDateTime: string
): string {
  return `Eff. ${dayjs.utc(isoDateTime).format("MMM D, YYYY")}`;
}

type FeeRateSource = { caption: string; ariaPriceModifier: string };

/** Shared: contract term `effectiveDate` ISO, or missing → Legacy. */
function feeRateSourceFromContractTermIso(
  effectiveDateIso: string | null | undefined
): FeeRateSource {
  if (effectiveDateIso == null || effectiveDateIso === "") {
    return {
      caption: "Legacy",
      ariaPriceModifier: "legacy rate (non-contract)",
    };
  }
  const caption = formatFeeContractEffectiveSublabel(effectiveDateIso);
  const when = dayjs.utc(effectiveDateIso).format("MMMM D, YYYY");
  return {
    caption,
    ariaPriceModifier: `contract effective ${when}`,
  };
}

/** Main grid row: `contractFeeSchedule` null → Legacy; else Eff. … from `effectiveDate`. */
export function feeRateSourceFromContractSchedule(
  contractFeeSchedule: { effectiveDate?: string } | null | undefined
): FeeRateSource {
  const iso =
    contractFeeSchedule == null ? undefined : contractFeeSchedule.effectiveDate;
  return feeRateSourceFromContractTermIso(iso);
}

/** Grouped grid / top10 payer cell: null → Legacy; else Eff. … */
export function feeRateSourceFromEffectiveDate(
  contractFeeScheduleEffectiveDate: string | null | undefined
): FeeRateSource {
  return feeRateSourceFromContractTermIso(contractFeeScheduleEffectiveDate);
}
